import { useEffect, useMemo, useState } from 'react';
import { Progress, WarningPanel } from '@backstage/core-components';
import { useApi, discoveryApiRef, errorApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { v4 as uuidv4 } from 'uuid';
import { useFileUpload } from './fileUploadHook';

import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import FolderIcon from '@material-ui/icons/Folder';
import Snackbar from '@material-ui/core/Snackbar';

import { UploadFilesFieldProps } from './UploadFilesFieldSchema';

export const UploadFilesField = ({ formData, onChange, rawErrors, schema, uiSchema }: UploadFilesFieldProps) => {
  const { componentName, projectSlug, s3Bucket, s3Prefix } = uiSchema['ui:options'] || {};
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const errorApi = useApi(errorApiRef);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [tags, setTags] = useState<string>('');
  const [componentWarning, setComponentWarning] = useState<string>();
  const [isLoadingTags, setIsLoadingTags] = useState<boolean>(true);

  // Generate a unique ID for each instance of the component
  const uploadId = useMemo(() => uuidv4(), []);

  // Use the custom file upload hook
  const { uploadFiles, removeFile, isLoadingFiles } = useFileUpload({
    componentName,
    projectSlug,
    s3Bucket,
    s3Prefix,
    tags,
    uploadId,
    formData,
    onChange,
    onError: message => setSnackbar({ open: true, message }),
  });

  useEffect(() => {
    if (!componentName && !projectSlug) {
      setComponentWarning(
        'componentName or projectSlug are required to determine the S3 prefix and tagging for the uploaded files. Please provide a value in the UI options (ui:options).',
      );
      setIsLoadingTags(false);
    }
  }, [componentName, projectSlug]);

  useEffect(() => {
    const fetchTags = async () => {
      if (!componentName && !projectSlug) return;
      setIsLoadingTags(true);
      const baseUrl = await discoveryApi.getBaseUrl('catalog');
      const queryParams = new URLSearchParams();
      if (componentName) {
        queryParams.append('componentName', componentName);
      }

      if (projectSlug) {
        queryParams.append('projectSlug', projectSlug);
      }
      const response = await fetchApi.fetch(
        `${baseUrl}/modules/component-info/v1/tagging/by-repo?${queryParams.toString()}`,
      );

      if (!response.ok) {
        setComponentWarning(
          `Failed to fetch tags. Please ensure the component exists in the catalog and review the configuration for projectSlug and componentName.`,
        );
        setIsLoadingTags(false);
        return;
      }

      const data = await response.json();

      const tagString = Object.entries(data.tags || {})
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      if (tagString.length === 0) {
        setComponentWarning(`Tags are missing. Please ensure the component is properly tagged in the catalog.`);
        setIsLoadingTags(false);
        return;
      }
      setTags(tagString);
      setComponentWarning(undefined);
      setIsLoadingTags(false);
    };
    fetchTags().catch(error => {
      setComponentWarning('Failed to fetch tags. Please try again later.');
      setIsLoadingTags(false);
      errorApi.post(error);
    });
  }, [componentName, discoveryApi, errorApi, fetchApi, projectSlug]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!componentName && !projectSlug) return;

    const files = event.target.files;
    if (!files) return;

    await uploadFiles(files);

    event.target.value = '';
  };

  const handleRemoveFile = async (index: number, file: string) => {
    await removeFile(index, file);
  };

  return (
    <>
      {componentWarning && (
        <WarningPanel title="Invalid component name" severity="warning" message={componentWarning} />
      )}

      {isLoadingTags && <Progress />}
      <FormControl margin="normal" fullWidth error={rawErrors?.length > 0 && !formData}>
        <input
          accept="*"
          multiple
          type="file"
          id={uploadId}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={!!componentWarning}
        />
        <label htmlFor={uploadId}>
          <Button variant="contained" component="span" disabled={!!componentWarning || isLoadingFiles}>
            Upload files
          </Button>
        </label>
        <FormHelperText>
          {schema.description || 'Choose files to upload. You can select multiple files.'}
        </FormHelperText>

        {formData && Array.isArray(formData) && formData.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle1">Uploaded files:</Typography>
            <List>
              {formData.map((file: string, index: number) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <FolderIcon />
                  </ListItemIcon>
                  <ListItemText>{file}</ListItemText>
                  <ListItemSecondaryAction>
                    <IconButton edge="end" aria-label="remove-file" onClick={() => handleRemoveFile(index, file)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </FormControl>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  );
};
